
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { GenericValidationsService } from 'src/app/Service/generic-validations.service';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { CONSTANTS } from '../../../../Core/Constant';
import { environment } from 'src/environments/environment';
import { TranslationService } from 'src/app/Service/translation.service';

@Component({
  selector: 'app-health-insurance',
  templateUrl: './health-insurance.component.html',
  styleUrls: ['./health-insurance.component.scss']
})
export class HealthInsuranceComponent {

  @Input() applicantData: any; // Accept data from parent component
  @Input() isPreviewMode: boolean = false; // Flag to determine behavior
  @Output() formGroupChange = new EventEmitter<FormGroup>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();

  healthInsuranceForm!: FormGroup;
  submitted = false;
  SaveMode = CONSTANTS.SAVE_MODE;
  isJsonShow = environment.isJsonShow;  // Set isJsonShow from the environment
  currentDate = new Date(); // For minDate in date picker
  isEditable: boolean = true; // Flag to track if the form is editable

  constructor(private toastr: ToastrService, private api: SevaProfileService, private fb: FormBuilder, public genericValidations: GenericValidationsService, private translationService: TranslationService) { }



  ngOnInit(): void {
    if (!this.isPreviewMode) {
      // Execute only when not in preview mode
      this.initializeForm();
      this.getApplicantData();
    }
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['applicantData'] && this.applicantData) {

      this.initializeForm();
      this.patchFormWithApplicantData(this.applicantData);
      this.healthInsuranceForm.disable();
    }
  }


  patchFormWithApplicantData(data: any): void {
    if (data) {
      // Handle any necessary data formatting before patching
      if (data.DateOfBirth) {
        data.DateOfBirth = this.genericValidations.formatDate(new Date(data.DateOfBirth));
      }
      if (data.ValidUntil) {
        data.ValidUntil = this.genericValidations.formatDate(new Date(data.ValidUntil));
      }
      this.healthInsuranceForm.patchValue(data);
    }
  }


  getApplicantData() {
    this.api.apiCall('HealthInsurance/GetHealthInsurance', 'GET').subscribe((response: any) => {
      if (response.Data && Object.keys(response.Data).length > 0) {
        this.genericValidations.setProgressPercentage(response.Data.CompletionPercentage); // Example: Set progress to 50%

        // Store IsEditable flag from response
        this.isEditable = response.Data.IsEditable !== false; // Default to true if not provided

        // Disable form if not editable
        if (!this.isEditable) {
          this.healthInsuranceForm.disable();
        } else {
          this.healthInsuranceForm.enable();
        }

        if (response.Data.ValidUntil) {
          response.Data.ValidUntil = this.genericValidations.formatDate(new Date(response.Data.ValidUntil));
        }

        this.healthInsuranceForm.patchValue(response.Data);
      }
    })
  }



  initializeForm() {
    this.healthInsuranceForm = this.fb.group({
      HasMedicalInsurance: [null, Validators.required],
      InsuranceCompany: ['', Validators.required],
      PolicyNumber: ['', Validators.required],
      ValidUntil: [null, Validators.required],
      HasHandleMedicalEmergency: [null, Validators.required],
      IsHealthInsuranceConcentGiven: [false, this.genericValidations.checkboxValidator('SaveMode', this.SaveMode.SAVE)],
      HealthInsuranceConcentGivenDate: [new Date().toISOString()],
      SaveMode: ['', [Validators.required,]],
    });


    const controlMappings = {
      'HasMedicalInsurance': ['InsuranceCompany', 'PolicyNumber', 'ValidUntil'],
      'walkDistance': ['walkDistanceReason'],

    };


    // Call the service with the isShowOn parameter set to true or false as needed
    this.genericValidations.setupFormListeners(this.healthInsuranceForm, controlMappings, true);

  }

  // Custom validator for checkbox that only validates when SaveMode is SAVE


  // Instead of method inside component
  shouldShowCheckboxValidation(): boolean {
    return this.genericValidations.shouldShowCheckboxValidation(
      this.healthInsuranceForm,
      'IsHealthInsuranceConcentGiven',
      'SaveMode',
      this.SaveMode.SAVE,
      this.submitted
    );
  }




  onSubmit(Mode: number): void {

    // this.healthInsuranceForm.patchValue({
    //   SaveMode: Mode
    // })

    // Trigger checkbox validation when SaveMode is SAVE
    if (Mode === this.SaveMode.SAVE) {
      const checkboxControl = this.healthInsuranceForm.get('IsHealthInsuranceConcentGiven');
      if (checkboxControl) {
        checkboxControl.updateValueAndValidity();
      }
    }

    // ✅ force re-check validation for checkbox
    this.healthInsuranceForm.get('IsHealthInsuranceConcentGiven')?.updateValueAndValidity();

    if (!this.healthInsuranceForm.valid && Mode == this.SaveMode.SAVE) {
      this.submitted = true;

      // Add 'submitted' class manually to all invalid controls
      Object.keys(this.healthInsuranceForm.controls).forEach((field) => {
        const control = this.healthInsuranceForm.get(field);
        if (control && control.invalid) {
          control.markAsTouched(); // Ensure ng-touched is triggered
          const element = document.getElementById(field);
          if (element) {
            element.classList.add('submitted'); // Add 'submitted' class to invalid elements
          }
        }
      });

      this.toastr.warning('Please complete all required fields before submitting.');


      return
    }


    const formData = this.healthInsuranceForm.value;

    // Convert DateOfBirth back to Date object before sending to the backend
    if (formData.ValidUntil) {
      formData.ValidUntil = new Date(formData.ValidUntil); // Convert back to Date object
    }

    if (formData.ValidUntil == "") {
      formData.ValidUntil = null
    }


    const cleanedValues = this.genericValidations.removeWhitespaceFromValues(this.healthInsuranceForm.value);
    this.addUpdate(cleanedValues, Mode);


  }

  addUpdate(reqBoby: any, Mode: number) {
    reqBoby.SaveMode = Mode;
    this.api.apiCall('HealthInsurance/SaveHealthInsurance', 'POST', reqBoby).subscribe((response: any) => {
      console.log('POST response:', response);
      this.toastr.success(response?.Message);
      if (Mode == this.SaveMode.SAVE) {
        this.nextStep.emit();
      } else if (Mode == this.SaveMode.DRAFT) {
        this.getApplicantData();
      }
    });
  }

  goToBack() {
    this.previous.emit();
  }

  getValidUntilControl(): FormControl {
    return this.healthInsuranceForm.get('ValidUntil') as FormControl;
  }

  // Translations
  getTranslation(key: string): string {
    return this.translationService.getTranslation(key);
  }
}
